import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  ArrowRightLeft,
  CircleAlert,
  GripVertical,
  LoaderCircle,
  Map,
  Save,
} from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { requireFirestore } from '../lib/firebase';
import {
  defaultRegionAssignments,
  regionsInArea,
  storedRegionAssignments,
  TERRITORIAL_AREAS,
  unassignedRegions,
  type RegionAssignments,
  type TerritorialAreaId,
} from '../lib/territorial-areas';
import { ITALIAN_REGIONS, type RegionCode } from '../lib/territorial-rules';

type SaveStatus = 'loading' | 'ready' | 'saving' | 'saved' | 'error';
type Region = typeof ITALIAN_REGIONS[number];
type DropZoneId = TerritorialAreaId | typeof unassignedDropId;

const settingsDocument = 'territorialAreas';
const demoStorageKey = 'report-crm:territorial-areas';
const unassignedDropId = 'unassigned';

function readDemoAssignments(): unknown {
  try {
    const stored = localStorage.getItem(demoStorageKey);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

function isRegionCode(value: string): value is RegionCode {
  return ITALIAN_REGIONS.some(({ code }) => code === value);
}

function isDropZoneId(value: string): value is DropZoneId {
  return value === unassignedDropId || TERRITORIAL_AREAS.some(({ id }) => id === value);
}

function RegionRow({ region, disabled }: { region: Region; disabled: boolean }) {
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: region.code,
    disabled,
  });

  return (
    <li
      ref={setNodeRef}
      className={isDragging ? 'dragging' : undefined}
      title={`Trascina ${region.name}`}
      aria-label={`Sposta ${region.name}`}
      {...attributes}
      {...listeners}
      aria-describedby="territorial-drag-help"
    >
      <GripVertical className="territorial-drag-handle" size={16} aria-hidden="true" />
      <span className="region-code">{region.code}</span>
      <strong>{region.name}</strong>
    </li>
  );
}

function TerritorialAreaCard({
  areaId,
  areaIndex,
  disabled,
  name,
  regions,
}: {
  areaId: TerritorialAreaId;
  areaIndex: number;
  disabled: boolean;
  name: string;
  regions: readonly Region[];
}) {
  const { isOver, setNodeRef } = useDroppable({ id: areaId, disabled });

  return (
    <section
      ref={setNodeRef}
      className={`territorial-area-card area-${areaIndex + 1}${isOver ? ' drop-target' : ''}`}
      aria-labelledby={`area-${areaId}`}
    >
      <header>
        <span>{String(areaIndex + 1).padStart(2, '0')}</span>
        <div>
          <h3 id={`area-${areaId}`}>{name}</h3>
          <p>{regions.length} {regions.length === 1 ? 'regione' : 'regioni'}</p>
        </div>
      </header>

      <ul className="territorial-area-regions">
        {regions.length === 0 && <li className="territorial-area-empty">Rilascia qui una regione</li>}
        {regions.map((region) => <RegionRow key={region.code} region={region} disabled={disabled} />)}
      </ul>
    </section>
  );
}

function UnassignedArea({ regions, disabled }: { regions: readonly Region[]; disabled: boolean }) {
  const { isOver, setNodeRef } = useDroppable({ id: unassignedDropId, disabled });

  return (
    <section
      ref={setNodeRef}
      className={`territorial-unassigned${isOver ? ' drop-target' : ''}`}
      aria-labelledby="unassigned-regions-title"
    >
      <header>
        <span><CircleAlert size={19} /></span>
        <div>
          <h3 id="unassigned-regions-title">Non allocati</h3>
          <p>{regions.length > 0
            ? `${regions.length} ${regions.length === 1 ? 'regione da assegnare' : 'regioni da assegnare'}`
            : 'Trascina qui una regione per rimuoverla dalla sua area.'}</p>
        </div>
      </header>
      <ul className="territorial-unassigned-regions">
        {regions.length === 0 && <li className="territorial-unassigned-empty">Nessuna regione non allocata</li>}
        {regions.map((region) => <RegionRow key={region.code} region={region} disabled={disabled} />)}
      </ul>
    </section>
  );
}

export function TerritorialAreasPage() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<RegionAssignments>(defaultRegionAssignments);
  const [status, setStatus] = useState<SaveStatus>('loading');
  const [dirty, setDirty] = useState(false);
  const [activeRegion, setActiveRegion] = useState<RegionCode | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    let active = true;
    const load = user?.demo
      ? Promise.resolve(readDemoAssignments())
      : getDoc(doc(requireFirestore(), 'settings', settingsDocument))
          .then((snapshot) => snapshot.data()?.assignments);

    void load
      .then((stored) => {
        if (!active) return;
        setAssignments(storedRegionAssignments(stored));
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [user?.demo]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [dirty]);

  const unassigned = useMemo(() => unassignedRegions(assignments), [assignments]);
  const assignedCount = ITALIAN_REGIONS.length - unassigned.length;

  const updateAssignment = (code: RegionCode, areaId: TerritorialAreaId | null) => {
    setAssignments((current) => ({ ...current, [code]: areaId }));
    setDirty(true);
    setStatus('ready');
  };

  const startDragging = ({ active }: DragStartEvent) => {
    const code = String(active.id);
    if (isRegionCode(code)) setActiveRegion(code);
  };

  const finishDragging = ({ active, over }: DragEndEvent) => {
    const code = String(active.id);
    const destination = over ? String(over.id) : '';
    if (isRegionCode(code) && isDropZoneId(destination)) {
      const nextArea = destination === unassignedDropId ? null : destination;
      if (assignments[code] !== nextArea) updateAssignment(code, nextArea);
    }
    setActiveRegion(null);
  };

  const save = async () => {
    setStatus('saving');
    try {
      if (user?.demo) {
        localStorage.setItem(demoStorageKey, JSON.stringify(assignments));
      } else {
        await setDoc(doc(requireFirestore(), 'settings', settingsDocument), {
          version: 1,
          areas: TERRITORIAL_AREAS.map(({ id, name }) => ({ id, name })),
          assignments,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email ?? null,
        }, { merge: true });
      }
      setDirty(false);
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="settings-tab-panel territorial-areas-page" aria-labelledby="territorial-areas-title">
      <header className="page-heading territorial-areas-heading">
        <div>
          <p className="app-eyebrow">Organizzazione geografica</p>
          <h2 id="territorial-areas-title">Aree territoriali</h2>
          <p>Raggruppa le regioni nelle quattro aree utilizzate per analisi e report.</p>
        </div>
      </header>

      <section className="territorial-areas-summary" aria-label="Riepilogo configurazione">
        <span className="territorial-areas-summary-icon"><Map size={22} /></span>
        <div>
          <strong>4 aree disponibili</strong>
          <p>Ogni regione può appartenere a una sola area.</p>
        </div>
        <dl>
          <div><dt>Assegnate</dt><dd>{assignedCount}</dd></div>
          <div className={unassigned.length > 0 ? 'attention' : undefined}><dt>Non allocati</dt><dd>{unassigned.length}</dd></div>
        </dl>
      </section>

      <div className="territorial-drag-help" id="territorial-drag-help">
        <GripVertical size={17} />
        <span><strong>Trascina e rilascia</strong> una regione sull’area di destinazione o in Non allocati.</span>
      </div>

      {status === 'error' && (
        <div className="territorial-feedback error" role="alert">
          Non è stato possibile leggere o salvare le aree territoriali. Riprova tra qualche istante.
        </div>
      )}

      {status === 'loading' ? (
        <div className="territorial-areas-loading" role="status">
          <LoaderCircle className="spin" size={22} /> Caricamento delle aree…
        </div>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            onDragStart={startDragging}
            onDragCancel={() => setActiveRegion(null)}
            onDragEnd={finishDragging}
          >
            <div className="territorial-areas-grid">
              {TERRITORIAL_AREAS.map(({ id, name }, areaIndex) => (
                <TerritorialAreaCard
                  key={id}
                  areaId={id}
                  areaIndex={areaIndex}
                  disabled={status === 'saving'}
                  name={name}
                  regions={regionsInArea(assignments, id)}
                />
              ))}
            </div>

            <UnassignedArea regions={unassigned} disabled={status === 'saving'} />

            <DragOverlay>
              {activeRegion && (() => {
                const region = ITALIAN_REGIONS.find(({ code }) => code === activeRegion);
                return region ? (
                  <div className="territorial-drag-overlay">
                    <GripVertical size={16} />
                    <span className="region-code">{region.code}</span>
                    <strong>{region.name}</strong>
                  </div>
                ) : null;
              })()}
            </DragOverlay>
          </DndContext>

          <footer className="territorial-areas-footer">
            <p>
              <ArrowRightLeft size={16} />
              {dirty
                ? 'Ci sono modifiche non salvate.'
                : status === 'saved'
                  ? 'Configurazione salvata correttamente.'
                  : 'Trascina le regioni tra le aree o in Non allocati, poi salva.'}
            </p>
            <button className="button button-primary" type="button" disabled={!dirty || status === 'saving'} onClick={() => void save()}>
              {status === 'saving' ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}
              {status === 'saving' ? 'Salvataggio…' : 'Salva configurazione'}
            </button>
          </footer>
        </>
      )}
    </section>
  );
}
